import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { axiosNoAuth } from '../config/axiosInstance';
import { NINJACURATE_BACKEND_BASE_URL } from '../config/api_config';

// Async thunk for fetching analysis data
export const fetchAnalysisData = createAsyncThunk(
    'newAnalysis/fetchAnalysisData',
    async (session_id, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.get(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/analysis`, {
                params: {
                    session_id: session_id
                },
                headers: {
                    'accept': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching analysis data:', error);
            const message = error?.response?.data || error?.message || 'Failed to fetch analysis data';
            toast.error('Failed to load analysis data');
            return rejectWithValue(message);
        }
    }
);


export const createOrderId = createAsyncThunk(
    'newAnalysis/createOrderId',
    async ({ session_id, product_id , addons , is_trial}) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/create_payment_test`, {
                session_id: session_id,
                product_id : product_id,
                addons : addons,
                is_trial : is_trial
            });
            return response.data;
        } catch (error) {
            console.error('Error creating order id:', error);
            toast.error('Failed to create order id');
        
        }
    }
);


export const fetchOrderSucess = createAsyncThunk(
    'newAnalysis/fetchOrderSucess',
    async ({ session_id, payment_id, subscription_id, razorpay_signature, errorObj }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/payment_success`, {
                session_id: session_id,
                payment_id: payment_id,
                subscription_id: subscription_id,
                isError: false,
                razorpay_signature: razorpay_signature || '',
                errorObj: errorObj || {}
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching order success:', error);
            toast.error('Failed to fetch order success');
        
        }
    }
);

export const fetchOrderFailed = createAsyncThunk(
    'newAnalysis/fetchOrderFailed',
    async ({ session_id, payment_id, subscription_id, razorpay_signature, errorObj }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/payment_failure"`, {
                session_id: session_id,
                payment_id: payment_id,
                subscription_id: subscription_id,
                isError: true,
                razorpay_signature: razorpay_signature,
                errorObj: errorObj
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching order failed:', error);
            toast.error('Failed to fetch order failed');
        
        }
    }
);

export const unlockCourse = createAsyncThunk(
    'newAnalysis/unlockCourse',
    async ({ session_id }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/trigger_course_curation`, {
                session_id: session_id,
                course_language: "English",
                is_onboarding: false
            });
            return response.data;
    } catch (error) {
        console.error('Error unlocking course:', error);
        toast.error('Failed to unlock course');
    }
});

const initialState = {
    analysisData: {
        correct_count: 0,
        wrong_count: 0,
        total_score: 0,
        user_level: '',
        subskill_performance: []
    },
    loading: false,
    error: null,
    isAnalysisPageVisited: false,
    isPricingPageVisited: false
};

// Redux slice for new analysis
const newAnalysisSlice = createSlice({
    name: 'newAnalysis',
    initialState,
    reducers: {
        clearAnalysisData: (state) => {
            state.analysisData = initialState.analysisData;
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        setIsAnalysisPageVisited: (state, action) => {
            state.isAnalysisPageVisited = action.payload;
        },
        setIsPricingPageVisited: (state, action) => {
            state.isPricingPageVisited = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch analysis data cases
            .addCase(fetchAnalysisData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAnalysisData.fulfilled, (state, action) => {
                state.loading = false;
                state.analysisData = action.payload;
                state.error = null;
            })
            .addCase(fetchAnalysisData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearAnalysisData,
    clearError,
    setIsAnalysisPageVisited,
    setIsPricingPageVisited
} = newAnalysisSlice.actions;

// Selectors
export const selectAnalysisData = (state) => state.newAnalysis.analysisData;
export const selectAnalysisLoading = (state) => state.newAnalysis.loading;
export const selectAnalysisError = (state) => state.newAnalysis.error;
export const selectCorrectCount = (state) => state.newAnalysis.analysisData.correct_count;
export const selectWrongCount = (state) => state.newAnalysis.analysisData.wrong_count;
export const selectTotalScore = (state) => state.newAnalysis.analysisData.total_score;
export const selectUserLevel = (state) => state.newAnalysis.analysisData.user_level;
export const selectSubskillPerformance = (state) => state.newAnalysis.analysisData.subskill_performance;
export const selectIsAnalysisPageVisited = (state) => state.newAnalysis.isAnalysisPageVisited;
export const selectIsPricingPageVisited = (state) => state.newAnalysis.isPricingPageVisited;

export default newAnalysisSlice.reducer;
